import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Upload } from 'antd';
import FetchUtils, { parseJSON } from '../../thunks/fetch_utils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { LoadingOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const fetchUtils = new FetchUtils();

const ChatUploader = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const { uploaderType } = props;
	const UploadComponent = useMemo(() => {
		if (uploaderType === 'dragger') {
			return Upload.Dragger;
		}
		return Upload;
	}, [uploaderType]);
	return (
		<div>
			<UploadComponent
				{...props}
				disabled={loading}
				customRequest={({ file, onError, onSuccess, onProgress }) => {
					const fileType = file.type;
					const fileName = file.name;

					setLoading(true);
					const preSignedURLEndpoint = `${getBackendUri()}/api/v1/${
						props.userType
					}/file/work_order_chat/upload/get_presigned_url/${props.id}`;
					fetchUtils
						.post(`${preSignedURLEndpoint}`, {
							data: {
								fileType,
								fileName,
							},
						})
						.then(parseJSON)
						.then(({ json }) => {
							const returnData = json.data;
							const signedRequest = returnData.presignedUrl;

							const uploadStartedAt = Date.now();

							// Use XMLHttpRequest for progress tracking
							const xhr = new XMLHttpRequest();

							xhr.upload.onprogress = (event) => {
								if (event.lengthComputable) {
									const percent = Math.round((event.loaded / event.total) * 100);
									onProgress({ percent });
								}
							};

							xhr.onload = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								if (xhr.status >= 200 && xhr.status < 300) {
									posthog.capture('file_upload_completed', {
										source: 'ChatUploader',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
									});
									const onSuccessPayload = {
										data: returnData.cloudFrontUrl,
									};
									onSuccess(onSuccessPayload);
								} else {
									const error = new Error(`Upload failed with status ${xhr.status}`);
									posthog.capture('file_upload_failed', {
										source: 'ChatUploader',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
										error: error.message,
									});
									onError(error);
									alert('ERROR ' + error.message);
								}
							};

							xhr.onerror = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								const error = new Error('Upload failed');
								posthog.capture('file_upload_failed', {
									source: 'ChatUploader',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
									error: error.message,
								});
								onError(error);
								alert('ERROR ' + error.message);
							};

							xhr.onabort = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								const error = new Error('Upload cancelled');
								posthog.capture('file_upload_cancelled', {
									source: 'ChatUploader',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
								});
								onError(error);
							};

							xhr.open('PUT', signedRequest);
							xhr.setRequestHeader('Content-Type', fileType);
							xhr.send(file);
						})
						.catch((error) => {
							alert(JSON.stringify(error));
						})
						.finally(() => {
							setLoading(false);
						});
				}}
			>
				<div>{props.children}</div>
			</UploadComponent>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		userType: state.session.userType,
	};
};

export default connect(mapStateToProps)(ChatUploader);
